import { Button, Flex, Form, Input, Select, Space, Switch } from 'antd';
import { FC } from 'react';

import { ButtonSuccess } from '@/common/components';
import { AppFormProps } from '@/common/interfaces';
import { defineInputStatus } from '@/common/utils';

import { DomainVisitors } from '@/modules/Domains';
import {
  REDIRECT_MATCH_MODE_OPTIONS,
  REDIRECT_STATUS_CODE_OPTIONS,
  RedirectEndpointEnum,
  RedirectFormFields,
  RedirectFormInputs,
} from '@/modules/Sites';
import { ArrowDown } from 'react-bootstrap-icons';

import { RedirectPageFromAutocomplete } from './RedirectPageFromAutocomplete';

interface Props extends AppFormProps<typeof RedirectFormInputs, RedirectFormFields> {}

// Шлях приводимо до нижнього регістру, але значення query-параметрів лишаємо як є (можуть бути регістрозалежні).
const normalizePath = (value?: string): string | undefined => {
  if (!value) return value;

  const queryIndex = value.indexOf('?');
  if (queryIndex === -1) return value.toLowerCase();

  return `${value.slice(0, queryIndex).toLowerCase()}${value.slice(queryIndex)}`;
};

export const RedirectsFormView: FC<Props> = ({
  formName,
  formType,
  form,
  initialValues,
  formErrors,
  isFormLoading,
  validationSchema,
  handleBack,
  handleFinish,
  handleFinishFailed
}) => {
  const siteId = String(initialValues?.siteId ?? '');

  const sourceType = Form.useWatch(RedirectFormInputs.SourceType, form) ?? initialValues?.sourceType;
  const targetType = Form.useWatch(RedirectFormInputs.TargetType, form) ?? initialValues?.targetType;

  const endpointTypeOptions = [
    { value: RedirectEndpointEnum.Node, label: 'Сторінка (нода)' },
    { value: RedirectEndpointEnum.Path, label: 'Шлях' },
  ];

  return (
    <Form
      form={ form }
      layout="vertical"
      name={ formName }
      initialValues={ initialValues }
      disabled={ isFormLoading }
      autoComplete="off"
      onFinish={ handleFinish }
      onFinishFailed={ handleFinishFailed }
    >
      <Form.Item hidden name={ RedirectFormInputs.SiteId }/>

      <Form.Item
        label="Джерело — тип"
        name={ RedirectFormInputs.SourceType }
        rules={ validationSchema.sourceType }
      >
        <Select options={ endpointTypeOptions }/>
      </Form.Item>

      { sourceType === RedirectEndpointEnum.Node ? (
        <Form.Item
          label="З (сторінка)"
          name={ RedirectFormInputs.SourceNodeId }
          hasFeedback
          help={ formErrors?.sourceNodeId }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.sourceNodeId) }
          rules={ validationSchema.sourceNodeId }
        >
          <RedirectPageFromAutocomplete siteId={ siteId } allowClear/>
        </Form.Item>
      ) : (
        <Form.Item
          label="З (шлях)"
          name={ RedirectFormInputs.SourcePath }
          hasFeedback
          help={ formErrors?.sourcePath }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.sourcePath) }
          rules={ validationSchema.sourcePath }
          normalize={ normalizePath }
        >
          <Input allowClear placeholder="/old-path або /old-path?id=1"/>
        </Form.Item>
      ) }

      <Form.Item noStyle>
        <Flex align="center" justify="center">
          <ArrowDown size={ 30 }/>
        </Flex>
      </Form.Item>

      <Form.Item
        label="Ціль — тип"
        name={ RedirectFormInputs.TargetType }
        rules={ validationSchema.targetType }
      >
        <Select options={ endpointTypeOptions }/>
      </Form.Item>

      { targetType === RedirectEndpointEnum.Node ? (
        <Form.Item
          label="До (сторінка)"
          name={ RedirectFormInputs.TargetNodeId }
          hasFeedback
          help={ formErrors?.targetNodeId }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.targetNodeId) }
          rules={ validationSchema.targetNodeId }
        >
          <RedirectPageFromAutocomplete siteId={ siteId } allowClear/>
        </Form.Item>
      ) : (
        <Form.Item
          label="До (шлях)"
          name={ RedirectFormInputs.TargetPath }
          hasFeedback
          help={ formErrors?.targetPath }
          validateStatus={ defineInputStatus(isFormLoading, formErrors?.targetPath) }
          rules={ validationSchema.targetPath }
          normalize={ normalizePath }
        >
          <Input allowClear placeholder="/new-path"/>
        </Form.Item>
      ) }

      <Form.Item
        required
        label="Код редіректу"
        name={ RedirectFormInputs.StatusCode }
        hasFeedback
        help={ formErrors?.statusCode }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.statusCode) }
        rules={ validationSchema.statusCode }
      >
        <Select options={ REDIRECT_STATUS_CODE_OPTIONS }/>
      </Form.Item>

      <Form.Item
        required
        label="Режим співставлення"
        name={ RedirectFormInputs.MatchMode }
        tooltip="Точний збіг — лише точний шлях (з query). Вкладеність — шлях і всі вкладені сторінки на одну ціль (шлях має існувати на сайті). Wildcard-редірект — префіксне переписування /one/* → /two/*."
        rules={ validationSchema.matchMode }
      >
        <Select
          options={ REDIRECT_MATCH_MODE_OPTIONS.map(({ value, label, description }) => ({
            value,
            label,
            title: description,
          })) }
        />
      </Form.Item>

      <Form.Item
        required
        label="Для відвідувача"
        name={ RedirectFormInputs.ForVisitor }
        hasFeedback
        help={ formErrors?.forVisitor }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.forVisitor) }
        rules={ validationSchema.forVisitor }
      >
        <Select allowClear>
          <Select.Option value={ DomainVisitors.Any }>Будь-хто</Select.Option>
          <Select.Option value={ DomainVisitors.GoogleBot }>Google Bot</Select.Option>
          <Select.Option value={ DomainVisitors.Person }>Людина</Select.Option>
        </Select>
      </Form.Item>

      <Form.Item
        label="Активна"
        layout="horizontal"
        name={ RedirectFormInputs.IsActive }
        hasFeedback
        help={ formErrors?.isActive }
        validateStatus={ defineInputStatus(isFormLoading, formErrors?.isActive) }
        rules={ validationSchema.isActive }
      >
        <Switch/>
      </Form.Item>

      <Form.Item wrapperCol={{ span: 24 }}>
        <Space align="center" style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button type="default" onClick={ handleBack }>
            Скасувати
          </Button>
          <ButtonSuccess loading={ isFormLoading } type="primary" htmlType="submit">
            { formType === 'edit' ? 'Зберегти' : 'Створити' }
          </ButtonSuccess>
        </Space>
      </Form.Item>
    </Form>
  );
};
